import {
  AbilityBuilder,
  createMongoAbility,
  MongoAbility,
} from "@casl/ability";
import type { Session } from "next-auth";
import {
  getSessionRoles,
  isAdminSession,
  isSupplierSession,
  isFinanceSession,
  isSapLinkedSession,
  isInternalSession,
  isIntegratorSession,
  getSupplierBrandCodes,
} from "../utils/pricingVisibility";
import { getAccountAccess } from "./accountAccess";

export type Actions =
  | "manage"
  | "read"
  | "create"
  | "update"
  | "delete"
  | "access"
  | "download"
  | "add";

export type Subjects =
  | "all"
  | "AdminPanel"
  | "SupplierPortal"
  | "FinancePortal"
  | "Price"
  | "Product"
  | "Order"
  | "UserManagement"
  | "Billing"
  | "TaxProfile"
  | "Cart"
  | "MyAccount"
  | "CommercialData"
  | "AdminBackoffice"
  | "AdminMarketing"
  | "AdminPricing"
  | "AdminEngineering"
  | "AdminProduct"
  | "AdminSales"
  | "AdminPurchaseOrders"
  | "AdminFinance"
  | "AdminSystem"
  | "AdminSync"
  | "AdminSalesIssues"
  | "AdminLogistics"
  | "AdminCache"
  | "AdminUserUnlock"
  | "AdminStockReservations"
  | "AdminSantanderMaintenance"
  | "AdminIntegratorRequests"
  | "AdminBrands"
  | "AdminPriceLists"
  | "AdminShippingConfig"
  | "AdminPartnerApi"
  // --- Granular product-page action subjects ---
  | "AdminProductSimulate"
  | "AdminProductDelete"
  | "AdminProductBulkDelete"
  | "AdminProductBulkVisibility"
  | "AdminProductUploadFile"
  | "AdminProductUploadCategories"
  | "AdminProductExport"
  | "AdminProductUploadVideos"
  | "AdminProductReindex";

export type PermissionString = `${Exclude<Subjects, "all">}.${Actions}`;

export interface PriceSubject {
  __type: "Price";
  brandCode?: string;
}

export type AppAbility = MongoAbility<[Actions, Subjects | PriceSubject]>;

/**
 * Resolves account roles from NextAuth session.
 */
export function getAccountRoles(session?: Session | null): string[] {
  const user = session?.user as Session["user"] & {
    tokenDecode?: { accountRoles?: string[] };
    user?: { accountRoles?: string[] };
    accountRoles?: string[];
  };
  const accountRoles = [
    ...(user?.tokenDecode?.accountRoles || []),
    ...(user?.user?.accountRoles || []),
    ...(user?.accountRoles || []),
  ];
  return [...new Set(accountRoles)];
}

/**
 * Detects the subject type of an object to support attribute-based authorization.
 */
export function detectSubjectType(subject: PriceSubject | string): Subjects {
  if (subject && typeof subject === "object" && "__type" in subject) {
    return subject.__type;
  }
  return typeof subject === "string" ? (subject as Subjects) : "all";
}

/**
 * Creates a default empty ability (public guest permissions).
 */
export function createEmptyAbility(): AppAbility {
  const { can, rules } = new AbilityBuilder<AppAbility>(createMongoAbility);
  // Guest can read basic product information
  can("read", "Product");
  return createMongoAbility(rules, {
    detectSubjectType,
  });
}

/**
 * Defines permissions that are independent from a React/NextAuth session.
 * This is intentionally reusable by the middleware, so route authorization
 * and the UI use the exact same CASL rules.
 */
export function defineAbilitiesForRoles(
  roles: string[] = [],
  accountRoles: string[] = [],
): AppAbility {
  const { can, rules } = new AbilityBuilder<AppAbility>(createMongoAbility);
  const normalizedRoles = [...new Set(roles)];

  if (normalizedRoles.includes("ROLE_ADMIN")) {
    can("manage", "all");
    return createMongoAbility(rules, {
      detectSubjectType,
    });
  }

  // ─── AdminPanel, AdminProduct, AdminPricing, AdminProductSimulate, AdminPriceLists ───
  // Every internal staff role can enter the /admin shell, access products catalog,
  // use the price simulator, and view price lists (read-only by default).
  if (
    normalizedRoles.some((role) =>
      [
        "ROLE_BACKOFFICE",
        "ROLE_FINANCE",
        "ROLE_MARKETING",
        "ROLE_PRICING",
        "ROLE_ENGINEERING",
        "ROLE_SALES",
        "ROLE_SYSTEMS",
        "ROLE_LOGISTICS",
        "ROLE_INTERNAL",
        "INTERNAL",
      ].includes(role),
    )
  ) {
    can("access", "AdminPanel");
    can("access", "AdminProduct");
    can("access", "AdminPricing");
    can("access", "AdminProductSimulate");
    can("access", "AdminPriceLists");
    can("read", "AdminPriceLists");
  }

  if (
    normalizedRoles.some((role) =>
      [
        "ROLE_BACKOFFICE",
        "ROLE_PRICING",
        "ROLE_FINANCE",
        "ROLE_MARKETING",
        "ROLE_SYSTEMS",
        "ROLE_LOGISTICS",
        "ROLE_SALES",
      ].includes(role),
    )
  ) {
    can("access", "AdminSync");
  }

  // --- AdminUserUnlock ---
  if (
    normalizedRoles.some((role) =>
      ["ROLE_BACKOFFICE", "ROLE_SALES", "ROLE_SYSTEMS"].includes(role),
    )
  ) {
    can("access", "AdminUserUnlock");
  }

  // --- AdminStockReservations (Productos > Stock reservado) ---
  // ROLE_ADMIN ya tiene "manage all" desde el bloque de arriba.
  if (
    normalizedRoles.some((role) =>
      ["ROLE_SALES", "ROLE_SYSTEMS", "ROLE_PRICING"].includes(role),
    )
  ) {
    can("access", "AdminStockReservations");
  }

  // ─── Role-specific subjects ──────────────────────────────────────────────
  if (normalizedRoles.includes("ROLE_BACKOFFICE")) {
    can("access", "AdminBackoffice");
    can("manage", "AdminBackoffice");
    can("access", "AdminIntegratorRequests");
    can("manage", "AdminIntegratorRequests");
  }

  if (normalizedRoles.includes("ROLE_MARKETING")) {
    // content-asset: /products/**, /notifications/**, /library/**, /admin/structured-pages/**
    // notification:  /admin/email-templates/**, /admin/event-catalog/**, /admin/delivery/**
    can("access", "AdminMarketing");
    can("access", "AdminBrands");
    can("access", "AdminCache");
  }

  if (normalizedRoles.includes("ROLE_PRICING")) {
    can("access", "AdminPricing");
    can("access", "AdminPriceLists");
    can("manage", "AdminPriceLists");
    can("access", "AdminProductSimulate");
    can("download", "AdminProductExport");
  }

  if (normalizedRoles.includes("ROLE_ENGINEERING"))
    can("access", "AdminEngineering");

  // product-catalog /admin/** accepts both ENGINEERING and MARKETING
  if (
    normalizedRoles.some((role) =>
      ["ROLE_ENGINEERING", "ROLE_MARKETING"].includes(role),
    )
  ) {
    can("access", "AdminProduct");
  }

  if (normalizedRoles.includes("ROLE_SALES")) {
    // purchasing-service: /admin/purchase-orders/** (not bank-transfer/refund)
    // business-operations: /service-feedback-requests, /project-contact-requests
    can("access", "AdminSales");
    can("access", "AdminSalesIssues");
    can("access", "AdminIntegratorRequests");
    can("access", "AdminBackoffice");
    can("read", "AdminBackoffice");
  }

  // Both Sales and Finance can consult the order list and its detail. Sales
  // retains its separate permissions for operational actions and issues.
  if (
    normalizedRoles.some((role) =>
      ["ROLE_SALES", "ROLE_FINANCE", "ROLE_LOGISTICS"].includes(role),
    )
  ) {
    can("access", "AdminPurchaseOrders");
  }

  if (normalizedRoles.includes("ROLE_FINANCE")) {
    // purchasing-service: /admin/purchase-orders/bank-transfer/**, /refund/**
    // payments-service:   /admin/**
    // sap-load-service:   /currency-rates/**
    can("access", "AdminFinance");
    can("access", "AdminSalesIssues"); // PurchaseOrders/issues
    can("access", "AdminSantanderMaintenance"); // purchasing-service: /admin/santander-maintenance-windows/** (ROLE_ADMIN, ROLE_FINANCE, ROLE_SYSTEMS)
  }

  // ─── ROLE_SYSTEMS / ROLE_LOGISTICS ───────────────────────────────────────
  if (normalizedRoles.includes("ROLE_SYSTEMS")) {
    can("access", "AdminSystem"); // ConfigureEmail + SyncProcessManagement
    can("access", "AdminCache"); // CacheInvalidation
    can("access", "AdminSalesIssues"); // PurchaseOrders/issues
    can("create", "AdminProductReindex"); // El backend de search indexation individual acepta Systems
    can("access", "AdminSantanderMaintenance"); // purchasing-service: /admin/santander-maintenance-windows/** (ROLE_ADMIN, ROLE_FINANCE, ROLE_SYSTEMS)
  }
  if (normalizedRoles.includes("ROLE_LOGISTICS")) {
    can("access", "AdminLogistics");
    can("access", "AdminShippingConfig");
    can("manage", "AdminShippingConfig");
    can("access", "AdminSalesIssues"); // PurchaseOrders/issues
  }

  // --- Granular product-page action permissions ---
  if (normalizedRoles.includes("ROLE_ENGINEERING")) {
    // Engineering can edit categories, relations, export products and view products
    can("create", "AdminProductUploadCategories");
    can("update", "AdminProductUploadCategories");
    can("delete", "AdminProductUploadCategories");
    can("download", "AdminProductExport");
  }
  if (normalizedRoles.includes("ROLE_MARKETING")) {
    // Marketing can edit product details, visibility, upload files/videos, reindex,
    // and delete individual products - DELETE /products/admin/{id} en el backend
    // exige {ROLE_ADMIN, ROLE_MARKETING}, no ENGINEERING como decía este comentario.
    can("update", "AdminProductBulkVisibility");
    can("create", "AdminProductUploadFile");
    can("download", "AdminProductExport");
    can("create", "AdminProductUploadVideos");
    can("create", "AdminProductReindex"); // Permitido
    can("delete", "AdminProductDelete");
  }

  // Base authenticated-user capabilities. Lo que depende del rol de cuenta
  // sale de getAccountAccess (misma fuente que middleware y menú).
  if (normalizedRoles.length > 0 || accountRoles.length > 0) {
    const accountAccess = getAccountAccess(accountRoles);
    can("read", "Product");
    can("access", "MyAccount");
    can("access", "CommercialData");
    if (accountAccess.orders) can("read", "Order");
    if (accountAccess.cart) {
      can("create", "Cart");
      can("add", "Cart");
    }
    if (accountAccess.taxProfile) can("access", "TaxProfile");
    if (accountAccess.billing) can("access", "Billing");
    if (accountAccess.userManagement) {
      can("access", "UserManagement");
      can("manage", "UserManagement");
    }
  }

  return createMongoAbility(rules, {
    detectSubjectType,
  });
}

/**
 * Factory function to define abilities dynamically based on session data.
 */
export function defineAbilitiesFor(session: Session | null): AppAbility {
  const { can, cannot, rules } = new AbilityBuilder<AppAbility>(
    createMongoAbility,
  );

  if (!session || !session.user) {
    // Guest permissions
    can("read", "Product");
    return createMongoAbility(rules, {
      detectSubjectType,
    });
  }

  const roles = getSessionRoles(session);
  const accountRoles = getAccountRoles(session);

  const isAdmin = isAdminSession(session) || roles.includes("ROLE_ADMIN");
  const isSupplier =
    isSupplierSession(session) || roles.includes("ROLE_SUPPLIER");
  const isFinance =
    isFinanceSession(session) ||
    roles.includes("ROLE_FINANCE") ||
    accountRoles.includes("ACCOUNT_FINANCE");
  const isSapLinked = isSapLinkedSession(session);
  const isInternal = isInternalSession(session);
  const isIntegrator = isIntegratorSession(session);

  // 1. System administrators can do everything. ACCOUNT_ADMIN is scoped to
  // its commercial account and must never become a system administrator.
  if (isAdmin) {
    can("manage", "all");
    return createMongoAbility(rules, {
      detectSubjectType,
    });
  }

  // Keep the administrative rules in sync with middleware and navigation.
  const roleAbility = defineAbilitiesForRoles(roles, accountRoles);
  rules.push(...roleAbility.rules);

  // 2. Baseline permissions for normal logged-in users. Pedidos, carrito,
  // perfil fiscal, facturación y gestión de usuarios dependen del rol de
  // cuenta y ya vienen en roleAbility (ver getAccountAccess).
  can("read", "Product");
  can("access", "MyAccount");
  can("access", "CommercialData");

  // 3. Internal User Baseline & Restrictions (Internal users can view prices, products catalog, and pricing simulator)
  if (isInternal) {
    can("access", "AdminPanel");
    can("access", "AdminProduct");
    can("access", "AdminPricing");
    can("access", "AdminProductSimulate");
    can("read", "Price");
    cannot("access", "SupplierPortal");
    cannot("access", "FinancePortal");
  } else {
    // 4. Finance Permissions
    if (isFinance) {
      can("access", "FinancePortal");
      can("read", "Price");
    }

    // 5. Supplier Permissions
    if (isSupplier) {
      can("access", "SupplierPortal");

      const supplierBrandCodes = getSupplierBrandCodes(session);
      if (supplierBrandCodes.length > 0) {
        // CASL's generic typing for `can(action, subject, conditions)` can't
        // resolve a usable conditions type here because our custom subject
        // tag (`__type`) isn't CASL's built-in tag key, so `ConditionsOf<>`
        // falls back to a shape TS can't match against a plain MongoQuery
        // object. This is a known CASL typing limitation, not a real type
        // hole: the object is a valid MongoQuery evaluated correctly at
        // runtime (used the same way already in defineAbilitiesFor above).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        can("read", "Price", { brandCode: { $in: supplierBrandCodes } } as any);
      }
    }

    // 6. SAP Linked / Integrator General Permissions
    if (isSapLinked || isIntegrator) {
      can("read", "Price");
    }
  }

  return createMongoAbility(rules, {
    detectSubjectType,
  });
}
